import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PauseCycles, PortalSubscription } from '@api/customer-portal-api';
import { Dialog, DialogContent, DialogTitle } from '@components/ui/dialog';
import { usePauseOptions, usePauseSubscription } from '@hooks/portal/useSubscriptions';
import { capturePortalEvent } from '@lib/monitoring';
import { nextChargeDiscount } from './discountOffer';
import { radioIndexForKey } from './frequencyOffer';
import { fmtAmount, fmtUtcDate, fmtSubscriptionFrequency } from './portalFormat';
import { pauseErrorMessage, pauseLengthLabel, subscriptionItems } from './subscriptionDetails';

interface Props {
  subscription: PortalSubscription;
  open: boolean;
  onClose: () => void;
  portalContainer: Element | null;
}

const failureDetails = (err: unknown) => {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } } | null)
    ?.response;
  const detail = response?.data?.detail;
  return {
    status: response?.status ?? 'network',
    reason: response?.status === 409 && typeof detail === 'string' ? detail : null,
  };
};

const PauseSubscriptionDialog: React.FC<Props> = ({
  subscription,
  open,
  onClose,
  portalContainer,
}) => {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<PauseCycles | null>(null);
  const [pauseErr, setPauseErr] = useState<string | null>(null);
  const optionsQuery = usePauseOptions(subscription.id, open);
  const pauseMutation = usePauseSubscription();
  const pausing = pauseMutation.isPending;
  // Set synchronously: two clicks in one tick both see the pre-render isPending=false.
  const inFlight = useRef(false);

  const eventProperties = {
    subscription_id: subscription.id,
    subscription_status: subscription.status,
  };

  // Each open starts clean. Adjusted during render, not in an effect, so the stale pick
  // never paints.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setPicked(null);
      setPauseErr(null);
    }
  }

  useEffect(() => {
    if (!open) return;
    capturePortalEvent('portal_pause_popup_opened', {
      subscription_id: subscription.id,
      subscription_status: subscription.status,
    });
    // Only a fresh open counts; a status refresh while open must not re-send it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (pausing) return;
    onClose();
  };

  const optionsError = optionsQuery.isError ? pauseErrorMessage(t, optionsQuery.error) : null;
  // A failed refetch keeps the last options in the cache; they're stale, so don't offer them.
  const options = optionsError ? undefined : optionsQuery.data;
  // The shortest length is picked by default; a pick that's no longer offered falls back to it.
  const offered = options?.options ?? [];
  const shortest = offered.reduce<PauseCycles | null>(
    (min, o) => (min === null || o.cycles < min ? o.cycles : min),
    null,
  );
  const selected = offered.some((o) => o.cycles === picked) ? picked : shortest;
  const items = subscriptionItems(subscription);
  const firstItem = items[0];
  const cents = nextChargeDiscount(subscription)?.discountedCents ?? subscription.amount;
  const selectedLabel =
    options && selected !== null
      ? pauseLengthLabel(t, selected, options.billing_period_days)
      : null;

  const submit = () => {
    if (selected === null || inFlight.current) return;
    inFlight.current = true;
    setPauseErr(null);
    pauseMutation.mutate(
      { subscriptionId: subscription.id, cycles: selected },
      {
        onSettled: () => {
          inFlight.current = false;
        },
        onSuccess: () => {
          capturePortalEvent('portal_subscription_paused', {
            ...eventProperties,
            pause_cycles: selected,
          });
          onClose();
        },
        onError: (err) => {
          capturePortalEvent('portal_subscription_pause_failed', {
            ...eventProperties,
            pause_cycles: selected,
            ...failureDetails(err),
          });
          setPauseErr(pauseErrorMessage(t, err));
        },
      },
    );
  };

  const confirmLabel = pausing
    ? t('pause.pausing')
    : selectedLabel
      ? t('pause.confirm', { length: selectedLabel })
      : t('subscriptionDetails.pauseButton');

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className="max-w-[420px] p-0 overflow-hidden rounded-[16px]"
        hideClose
        container={portalContainer}
        aria-describedby={undefined}
      >
        <div className="px-6 pt-6 pb-2">
          <div className="flex items-center gap-3 mb-3">
            {firstItem?.product_image && (
              <img
                src={firstItem.product_image}
                alt=""
                className="w-10 h-10 rounded-[8px] object-cover flex-shrink-0"
              />
            )}
            <div className="min-w-0">
              <p className="text-[13px] font-medium text-[#111827] truncate">
                {firstItem?.name ?? t('dashboard.subscriptionFallbackName')}
              </p>
              {items.length >= 2 && (
                <p className="text-[12px] text-[#6B7280]">
                  {t('pause.moreItems', { count: items.length - 1 })}
                </p>
              )}
              <p className="text-[12px] text-[#6B7280]">
                {fmtAmount(cents, subscription.currency)} · {fmtSubscriptionFrequency(subscription)}
              </p>
            </div>
          </div>
          <DialogTitle className="text-[18px] primary-font-semibold text-[#111827] mb-1">
            {t('pause.title')}
          </DialogTitle>

          {optionsQuery.isLoading && (
            <p className="text-[13px] text-[#6B7280] py-4">{t('pause.loading')}</p>
          )}
          {optionsError && <p className="text-[13px] text-red-600 py-2">{optionsError}</p>}
          {options && (
            <>
              <p className="text-[13px] text-[#6B7280]">
                {t('pause.body', { date: fmtUtcDate(options.current_next_payment_date) })}
              </p>
              <div
                role="radiogroup"
                aria-label={t('pause.optionsLabel')}
                className="flex flex-col gap-2 mt-4"
                onKeyDown={(e) => {
                  const lengths = options.options;
                  const current = lengths.findIndex((o) => o.cycles === selected);
                  const next = radioIndexForKey(Math.max(current, 0), e.key, lengths.length);
                  if (next === null || pausing) return;
                  e.preventDefault();
                  setPicked(lengths[next].cycles);
                  e.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]')[next]?.focus();
                }}
              >
                {options.options.map((o) => {
                  const checked = selected === o.cycles;
                  return (
                    <button
                      key={o.cycles}
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      tabIndex={checked ? 0 : -1}
                      disabled={pausing}
                      onClick={() => setPicked(o.cycles)}
                      className="flex flex-col items-start text-left px-4 py-3 rounded-[10px] border transition-colors disabled:opacity-50"
                      style={{
                        borderColor: checked ? '#111827' : '#E5E5E5',
                        backgroundColor: checked ? '#F5F5F5' : '#FFFFFF',
                      }}
                    >
                      <span className="text-[14px] font-medium text-[#111827]">
                        {pauseLengthLabel(t, o.cycles, options.billing_period_days)}
                      </span>
                      <span className="text-[12px] text-[#6B7280]">
                        {t('pause.optionNextOrder', { date: fmtUtcDate(o.next_payment_date) })}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {/* After a 409 the options refetch fails with the same reason; say it once. */}
          {pauseErr && pauseErr !== optionsError && (
            <p className="text-[12px] text-red-600 mt-3">{pauseErr}</p>
          )}
        </div>
        <div className="flex gap-3 px-6 pb-6 pt-4">
          <button
            onClick={handleClose}
            disabled={pausing}
            className="flex-1 py-3 rounded-[10px] text-[14px] font-medium text-[#374151] bg-[#F5F5F5] hover:bg-[#EBEBEB] transition-colors disabled:opacity-50"
          >
            {t('pause.neverMind')}
          </button>
          {options && (
            <button
              onClick={submit}
              disabled={selected === null || pausing}
              className="flex-1 py-3 rounded-[10px] text-[14px] font-medium text-white hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ backgroundColor: '#111827' }}
            >
              {confirmLabel}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PauseSubscriptionDialog;
